import { IsIn, IsNumber, IsOptional, IsString, Max, MaxLength, Min } from 'class-validator';

/**
 * Visit check-ins — an officer's trip to a file's property.
 *
 * A visit moves `en_route → on_site → completed`, and may be `cancelled` from
 * either of the first two. Each transition carries at most one GPS fix, taken
 * by the device at the moment of the tap; the fix is optional so a visit can
 * always be recorded, signal or not.
 *
 * Officer-only: these shapes are served by gov-api and never by the public API.
 */

export type VisitStatus = 'en_route' | 'on_site' | 'completed' | 'cancelled';
export const VISIT_STATUSES: VisitStatus[] = ['en_route', 'on_site', 'completed', 'cancelled'];

/** The statuses a visit can be moved *to* after it has been started. */
export type VisitTransition = Exclude<VisitStatus, 'en_route'>;
export const VISIT_TRANSITIONS: VisitTransition[] = ['on_site', 'completed', 'cancelled'];

/** A visit that is still under way: at most one per officer per file. */
export const OPEN_VISIT_STATUSES: readonly VisitStatus[] = ['en_route', 'on_site'];

/** Which statuses each status may move to. Terminal statuses move nowhere. */
export const VISIT_NEXT: Record<VisitStatus, readonly VisitTransition[]> = {
  en_route: ['on_site', 'cancelled'],
  on_site: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

export function canTransitionVisit(from: VisitStatus, to: VisitTransition): boolean {
  return VISIT_NEXT[from].includes(to);
}

/** One device fix. Null fields mean the device gave no fix. */
export interface VisitFix {
  at: string;
  lat: number | null;
  lng: number | null;
  /** Horizontal accuracy in metres, as reported by the device. */
  accuracy: number | null;
}

/**
 * The route proposed when the visit started, from the set-off fix to the
 * property. A plan, not a track: it says nothing about the road actually taken.
 */
export interface VisitRoute {
  /** Encoded polyline (Google polyline algorithm, precision 5). */
  polyline: string | null;
  distanceMeters: number;
  durationSeconds: number;
  eta: string;
  /** True for the straight-line fallback (no routing key, or routing failed). */
  estimated: boolean;
  provider: 'google' | 'estimate';
}

export interface VisitDetail {
  id: string;
  fileId: string;
  officerId: string | null;
  /** Display name of the officer, resolved server-side. */
  officerName: string | null;
  status: VisitStatus;
  enRoute: VisitFix | null;
  onSite: VisitFix | null;
  completed: VisitFix | null;
  cancelled: VisitFix | null;
  /** Null when there was no set-off fix, or the file has no coordinates. */
  route: VisitRoute | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Payload of the realtime `visit.updated` event. */
export interface VisitUpdatedPayload {
  fileId: string;
  visit: VisitDetail;
}

/**
 * The position fields every transition accepts. Bounded to the real ranges so a
 * garbage fix (a device reporting 9999) is refused rather than stored as
 * evidence of where the officer stood.
 */
class VisitFixFields {
  @IsOptional()
  @IsNumber({ allowNaN: false, allowInfinity: false })
  @Min(-90)
  @Max(90)
  lat?: number | null;

  @IsOptional()
  @IsNumber({ allowNaN: false, allowInfinity: false })
  @Min(-180)
  @Max(180)
  lng?: number | null;

  @IsOptional()
  @IsNumber({ allowNaN: false, allowInfinity: false })
  @Min(0)
  @Max(100_000)
  accuracy?: number | null;

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  notes?: string | null;
}

/** `POST /files/:fileId/visits` — the officer sets off. */
export class StartVisitDto extends VisitFixFields {}

/** `POST /files/:fileId/visits/:id/transition` — arrived, finished, called off. */
export class TransitionVisitDto extends VisitFixFields {
  @IsIn(VISIT_TRANSITIONS)
  status!: VisitTransition;
}
